import { StopIcon } from "features/CenterContent/shared/StopIcon";
import {
  BusStopGridCell,
  BusStopGridRow,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/BusStopGrid";
import { BusTimes } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/ItineraryDetails";
import {
  isStopCancelled,
  timeFromKeys,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/utils";
import { FC } from "react";
import { Flex, HStack, Text, tokens, VStack } from "shared/components/Spor";
import colors from "shared/theme/colors";
import { ColorTheme } from "shared/theme/theme";
import {
  AlternativeTransportItineraryStop,
  StopActivity,
  VehicleStatus,
} from "shared/types/alternativeTransport";
import styled, { useTheme } from "styled-components";

const IconCellWrapper = styled(VStack)`
  height: 100%;
  position: relative;
  margin-top: ${tokens.default.size.spacing[0.5]};
  left: 1px;
`;

const Line = styled(Flex)`
  position: absolute;
  top: -16px;
  height: calc(100% + 33px);
  width: 1px;
  z-index: 0;
`;

const getActivityReadable = (cancelled: boolean, activity: StopActivity) => {
  if (cancelled) return "Innstilt på stopp";
  switch (activity) {
    case "NONE":
      return "Passerer";
    case "BOARDING":
      return "Påstigning";
    case "DISEMBARKING":
      return "Avstigning";
    case "BOARDING_AND_DISEMBARKING":
      return "På- og avstigning";
  }
};

const getStopColor = (
  cancelled: boolean,
  activity: StopActivity,
  theme: ColorTheme,
) => {
  if (cancelled) return theme.colorIconMain;
  switch (activity) {
    case "BOARDING":
      return colors.primaryGreen;
    case "DISEMBARKING":
      return theme.colorAlarm;
    case "BOARDING_AND_DISEMBARKING":
    case "NONE":
      return theme.colorIconMain;
  }
};

export type BusStopRowProps = {
  stop: AlternativeTransportItineraryStop;
  index: number;
  vehicleStatus: VehicleStatus;
  currentStopIndex: number;
};

export const BusStopRow: FC<BusStopRowProps> = ({
  stop,
  index,
  vehicleStatus,
  currentStopIndex,
}) => {
  const theme = useTheme();
  const cancelled = isStopCancelled(stop, vehicleStatus);
  const stopColor = getStopColor(cancelled, stop.activity, theme);
  const lineColor = theme.colorIconMain;
  const isCurrentStop = index === currentStopIndex;
  const isFutureStop = index > currentStopIndex;

  const arrivalTimes = {
    actualOrEstimatedTime: timeFromKeys(stop, [
      "actualArrivalTime",
      "estimatedArrivalTime",
    ]),
    scheduledTime: timeFromKeys(stop, ["scheduledArrivalTime"]),
  };

  const departureTimes = {
    actualOrEstimatedTime: timeFromKeys(stop, [
      "actualDepartureTime",
      "estimatedDepartureTime",
    ]),
    scheduledTime: timeFromKeys(stop, ["scheduledDepartureTime"]),
  };

  return (
    <BusStopGridRow $isCurrentStop={isCurrentStop} role="row">
      <BusStopGridCell role="cell">
        <IconCellWrapper>
          <Flex
            alignItems="center"
            justifyContent="center"
            style={{
              position: "relative",
              zIndex: 1,
              backgroundColor: isCurrentStop
                ? theme.colorSelected
                : theme.colorBackgroundSecondary,
            }}
          >
            <StopIcon
              justPassingThrough={stop.activity === "NONE"}
              isCancelled={cancelled}
              color={stopColor}
              fill
              height={19}
              width={21}
            />
          </Flex>
          <Line backgroundColor={lineColor} />
        </IconCellWrapper>
      </BusStopGridCell>
      <BusStopGridCell role="cell">
        <VStack justifyContent="center" alignItems="" gap={0}>
          <HStack justifyContent="space-between">
            <Text
              variant="sm"
              title={stop.name ?? undefined}
              fontWeight={700}
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {stop.name}
            </Text>
          </HStack>
          <Text
            variant="sm"
            title={stop.name ?? undefined}
            fontWeight={400}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {getActivityReadable(cancelled, stop.activity)}
          </Text>
        </VStack>
      </BusStopGridCell>
      <BusStopGridCell role="cell">
        <BusTimes {...arrivalTimes} isFutureStop={isFutureStop} />
      </BusStopGridCell>
      <BusStopGridCell role="cell">
        <BusTimes {...departureTimes} isFutureStop={isFutureStop} />
      </BusStopGridCell>
    </BusStopGridRow>
  );
};
